/**
 * Download utility using server-side temp file storage.
 * 
 * Instead of handling binary data in JavaScript (which causes issues
 * with blob URLs in Chrome and other browsers), this utility:
 * 1. POSTs the request data to the API endpoint
 * 2. The server generates the file, stores it, and returns a download URL
 * 3. A hidden <a> link triggers a native file download via the GET URL
 * 
 * The server's GET endpoint sets Content-Disposition with the proper filename,
 * ensuring correct filenames across all browsers.
 */

/**
 * Generate a file on the server and trigger a native browser download.
 * 
 * @param {string} url - The API endpoint URL (relative or absolute)
 * @param {object} body - The JSON request body
 * @param {string} fallbackFilename - Fallback filename (unused by server, kept for compat)
 */
export async function downloadFile(url, body, fallbackFilename) {
    // Get auth token
    const token = localStorage.getItem('saige_token');

    // Determine base URL (same fail-fast logic as api.js)
    const isDev = import.meta.env.DEV;
    let baseURL;
    if (import.meta.env.VITE_API_URL) {
        baseURL = import.meta.env.VITE_API_URL;
    } else if (isDev) {
        baseURL = 'http://localhost:8000';
    } else {
        console.warn('[Saige] VITE_API_URL not set. Using same-origin for API calls.');
        baseURL = '';
    }
    const fullUrl = url.startsWith('http') ? url : `${baseURL}${url}`;

    // Step 1: POST to generate the file (returns JSON with download_url)
    // Add a 30-second timeout via AbortController
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000);

    let response;
    try {
        response = await fetch(fullUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
            },
            body: JSON.stringify(body),
            signal: controller.signal,
        });
    } catch (fetchErr) {
        clearTimeout(timeoutId);
        if (fetchErr.name === 'AbortError') {
            throw new Error('Request timed out after 30 seconds. Please try again.');
        }
        throw new Error(`Network error: ${fetchErr.message}`);
    } finally {
        clearTimeout(timeoutId);
    }

    if (!response.ok) {
        // Try to extract a meaningful error message
        let errorMessage = `File generation failed (${response.status})`;
        try {
            const contentType = response.headers.get('content-type') || '';
            if (contentType.includes('application/json')) {
                const errorJson = await response.json();
                errorMessage = errorJson.detail || errorJson.error || errorJson.message || errorMessage;
            } else {
                const errorText = await response.text();
                if (errorText.length < 200) {
                    errorMessage = errorText || errorMessage;
                }
            }
        } catch {
            // Ignore parse errors, use default message
        }
        throw new Error(errorMessage);
    }

    // Check content type — if server returned JSON, extract download_url
    const contentType = response.headers.get('content-type') || '';

    if (contentType.includes('application/json')) {
        const result = await response.json();

        // Check for server-side error in JSON response
        if (result.error && !result.download_url) {
            throw new Error(result.error);
        }

        if (!result.download_url) {
            throw new Error('Server did not return a download URL');
        }

        // Step 2: Trigger native browser download via hidden <a> link
        const downloadUrl = result.download_url.startsWith('http')
            ? result.download_url
            : `${baseURL}${result.download_url}`;

        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = result.filename || fallbackFilename || '';
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();

        // Small delay before cleanup to let the browser initiate the download
        await new Promise(resolve => setTimeout(resolve, 500));
        document.body.removeChild(link);

        // Return the result in case the caller needs the document_id or other data
        return result;
    } else {
        // Server returned a direct file response (binary) — handle as blob download
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);

        // Try to extract filename from Content-Disposition header
        let filename = fallbackFilename || 'download';
        const disposition = response.headers.get('content-disposition');
        if (disposition) {
            const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
            if (match && match[1]) {
                filename = match[1].replace(/['"]/g, '');
            }
        }

        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = filename;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();

        await new Promise(resolve => setTimeout(resolve, 500));
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);

        return { filename };
    }
}

export default downloadFile;
